import { PrimaryButton, RegularButtonWrapper } from '@monthio/react-components'
import { useState, VoidFunctionComponent } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import { CustomBankAccount } from './CustomBankAccount'
import {
	getFromLocalStorage,
	setLocalStorage,
} from './customProfileUtils'
import { FlowPage } from '@monthio/design-system'
import { routeCodes } from '#modules/Bank/Bank.types'

const CustomBankProfileCreationContainer = styled.div`
	display: flex;
	flex-flow: column nowrap;

	> div:not(:first-child) {
		margin-top: 8px;
	}
`

const RecentlyUsedCustomBankProfileCreationContainer = styled(
	CustomBankProfileCreationContainer,
)`
	word-break: break-all;

	> :not(:first-child) {
		margin-top: 8px;
	}
`

export const GridLayout = styled.div`
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 12px;
	column-gap: 12px;
	padding: 8px;
`

const defaultCustomBankProfile = {
	income: 25000,
	incomeVariability: 'none' as 'none' | 'low' | 'medium' | 'high',
	twoIncomeStreams: false,
	housing: -6000,
	housingType: 'rent' as 'rent' | 'own',
	transport: -2000,
	loans: -1500,
	others: -4000,
	gambling: false,
	childIndicator: false,
	debtCollection: false,
	publicDebtCollection: false,
	nrExtraTransactions: 0,
	hasSecondAccount: false,
	hasAdditionalIncome: false,
	ingoingForeignTransfers: 200,
	outgoingForeignTransfers: 300,
	cashWithdrawals: 500,
}

export type CustomBankProfile = typeof defaultCustomBankProfile

export const CustomProfileCreation: VoidFunctionComponent<{
	monthioInternalBankId: number | null
}> = ({ monthioInternalBankId }) => {
	const { t } = useTranslation()
	const history = useHistory()
	const [customProfile, setCustomProfile] = useState(defaultCustomBankProfile)

	const updateCustomProfile = (
		key: keyof CustomBankProfile,
		value: string | number | boolean,
	) => {
		const updatedCustomProfile = { ...customProfile, [key]: value }
		setCustomProfile(updatedCustomProfile)
	}

	const handleProfileSelection = (account: CustomBankProfile) => {
		setRecentlyUsed(account)
		history.replace({
			pathname: routeCodes.bankSessionOverviewPage,
			state: {
				consentCode: JSON.stringify(customProfile),
				monthioInternalBankId,
				authUrl: 'customBank',
			},
		})
	}

	return (
		<FlowPage.Content>
			<GridLayout>
				<CustomBankProfileCreationContainer>
					<CustomBankAccount
						customProfileInputs={customProfile}
						updateProfile={updateCustomProfile}
					/>
					<RegularButtonWrapper>
						<PrimaryButton
							onClick={() => handleProfileSelection(customProfile)}
						>
							{t('btn-add')}
						</PrimaryButton>
					</RegularButtonWrapper>
				</CustomBankProfileCreationContainer>
				<RecentlyUsedCustomBankProfileCreationContainer>
					{getRecentlyUsed().map((profile) => (
						<PrimaryButton
							key={JSON.stringify(profile)}
							onClick={() => handleProfileSelection(profile)}
						>
							{`${t('customProfileCreation-label-income')}: ${profile.income}`}
							{`${t('customProfileCreation-label-housing')}: ${profile.housing}`}
							{t(`customProfileCreation-housingType-${profile.housingType}`)}
							{`${t('customProfileCreation-label-transport')}: ${
								profile.transport
							}`}
							{`${t('customProfileCreation-label-loans')}: ${profile.loans}`}
							{`${t('customProfileCreation-label-others')}: ${profile.others}`}
							{profile.gambling
								? t('customProfileCreation-label-gambling')
								: null}
							{profile.childIndicator
								? t('customProfileCreation-label-childIndicator')
								: null}
							{profile.debtCollection
								? t('customProfileCreation-label-debtCollection')
								: null}
							{profile.publicDebtCollection
								? t('customProfileCreation-label-publicDebtCollection')
								: null}
						</PrimaryButton>
					))}
				</RecentlyUsedCustomBankProfileCreationContainer>
			</GridLayout>
		</FlowPage.Content>
	)
}

const STORAGE_KEY = 'CUSTOM_BANK_PROFILES_RECENTLY_USED'
const setRecentlyUsed = (customBankProfile: CustomBankProfile) => {
	const profileMap = new Map<string, CustomBankProfile>()
	getRecentlyUsed().forEach((profile) =>
		profileMap.set(JSON.stringify(profile), profile),
	)

	profileMap.set(JSON.stringify(customBankProfile), customBankProfile)
	setLocalStorage(STORAGE_KEY, [...profileMap.values()].slice(0, 9))
}

const getRecentlyUsed = () => {
	return getFromLocalStorage<CustomBankProfile[]>(STORAGE_KEY, [])
}
